import React from 'react';
import {
  Database, CheckCircle2, Activity, Hash, Sparkles, Cloud,
} from 'lucide-react';
import type { CorpusStats } from './types';
import { formatRelative } from './types';

interface Props { stats: CorpusStats; }

interface TileSpec {
  tone: string;          // hex
  bg: string;            // tailwind bg-* with arbitrary value
  text: string;          // tailwind text-* with arbitrary value
  icon: React.ReactNode;
  label: string;
  value: string | number;
}

/** Six-tile KPI rail. 2 → 3 → 6 columns. */
export const KPIStrip: React.FC<Props> = ({ stats }) => {
  const tiles: TileSpec[] = [
    { tone: '#1a5276', bg: 'bg-[#1a5276]/10', text: 'text-[#1a5276]',
      icon: <Database size={16} />,    label: 'Papers',      value: stats.total_papers },
    { tone: '#0e9172', bg: 'bg-[#0e9172]/10', text: 'text-[#0e9172]',
      icon: <CheckCircle2 size={16} />, label: 'Ready',       value: stats.ready },
    { tone: '#2e86ab', bg: 'bg-[#2e86ab]/10', text: 'text-[#2e86ab]',
      icon: <Activity size={16} />,    label: 'Processing',  value: stats.processing },
    { tone: '#b78600', bg: 'bg-[#b78600]/10', text: 'text-[#b78600]',
      icon: <Hash size={16} />,        label: 'Total chunks', value: stats.total_chunks.toLocaleString() },
    { tone: '#6c3483', bg: 'bg-[#6c3483]/10', text: 'text-[#6c3483]',
      icon: <Sparkles size={16} />,    label: 'Questions',   value: stats.total_questions.toLocaleString() },
    { tone: '#536070', bg: 'bg-slate-100',    text: 'text-slate-600',
      icon: <Cloud size={16} />,       label: 'Last upload', value: stats.last_upload ? formatRelative(stats.last_upload) : '—' },
  ];

  return (
    <div className="mb-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5">
      {tiles.map((t, i) => (
        <div
          key={i}
          className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-3.5 py-3"
        >
          <span className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl ${t.bg} ${t.text}`}>
            {t.icon}
          </span>
          <div className="min-w-0">
            <p
              className="m-0 truncate text-[22px] leading-none text-slate-900"
              style={{ fontFamily: "'Instrument Serif', serif" }}
            >
              {t.value}
            </p>
            <p className="m-0 mt-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
              {t.label}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
};
